import React from 'react';
import Modal from './Modal';
import {formatBytes} from '../../lib/audioMetadata';
import {AlertTriangleIcon, TrashIcon, UploadCloudIcon} from '../Icons';

/**
 * Why a resume was refused, and therefore what is on offer instead.
 *
 * `mismatch` — the file picked is not the one this upload started with. The user has the wrong
 * file, or has re-exported the same episode since. Either way the parts already in R2 belong to a
 * different recording, and stitching the two together would produce an object that completes
 * cleanly and is silently corrupt, so the only honest options are "upload this one from scratch" or
 * "go and find the original".
 *
 * `dead` — the upload cannot be continued at all: the multipart upload is gone, or the row predates
 * resumable uploads. Nothing here is recoverable, so the offer is to clear it out.
 */
export type ResumeRefusalKind = 'mismatch' | 'dead';

interface ResumeUploadModalProps {
  kind: ResumeRefusalKind | null;
  /** The half-finished upload's own filename, which is the one the user was expected to pick. */
  originalName: string;
  /** What they picked instead — only shown for a mismatch, where the difference is the point. */
  pickedName: string;
  /** The server's own sentence, shown as the detail line rather than paraphrased. */
  message: string;
  /** Size of the file this upload started with, when the server said. Mismatch only. */
  expectedSize?: number;
  /** Size of the file they actually picked, for the same comparison. */
  pickedSize: number;
  isBusy: boolean;
  /** Mismatch only: upload the file they just picked as a new file. */
  onStartNewUpload: () => void;
  /** Dead only: delete the stranded row, then open the picker for a fresh upload. */
  onDeleteAndReupload: () => void;
  onCancel: () => void;
}

const secondaryButton = 'rounded-lg border border-gray-700 px-3 py-1.5 text-xs font-medium '
  + 'text-gray-200 hover:border-gray-600 hover:bg-gray-800 transition-colors cursor-pointer '
  + 'disabled:opacity-40 disabled:cursor-not-allowed';

const ResumeUploadModal: React.FC<ResumeUploadModalProps> = ({
  kind,
  originalName,
  pickedName,
  message,
  expectedSize,
  pickedSize,
  isBusy,
  onStartNewUpload,
  onDeleteAndReupload,
  onCancel,
}) => {
  const isMismatch = kind === 'mismatch';

  return (
    <Modal
      isOpen={kind !== null}
      onClose={onCancel}
      title={isMismatch ? 'That’s a different file' : 'This upload can’t be resumed'}
    >
      <div className="space-y-4">
        <div className="flex gap-3">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div className="min-w-0 space-y-2 text-sm text-gray-300">
            {isMismatch ? (
              <>
                <p>
                  <span className="font-medium text-gray-100">{pickedName}</span> isn’t the file this
                  upload started with. Carrying on would mix parts of two different recordings into
                  one file, so it has been stopped here instead.
                </p>
                <p className="text-gray-400">
                  To carry on where it left off, pick{' '}
                  <span className="font-medium text-gray-200">{originalName}</span> — the same copy,
                  not a re-export. Otherwise upload the file you just chose as a new one.
                </p>
                {/* The sizes are the fastest way to work out which copy is the right one, and the
                    server only sends the expected size when size (not mtime) is what differed. */}
                {expectedSize !== undefined && (
                  <p className="text-xs tabular-nums text-gray-500">
                    This upload started with a {formatBytes(expectedSize)} file; you picked{' '}
                    {formatBytes(pickedSize)}.
                  </p>
                )}
              </>
            ) : (
              <>
                <p>
                  <span className="font-medium text-gray-100">{originalName}</span> can’t be carried
                  on. Unfinished uploads are swept up after two days, and this one is past that or
                  was never resumable to begin with.
                </p>
                <p className="text-gray-400">
                  Nothing was charged for it. Delete the entry and upload the file again.
                </p>
              </>
            )}
            {message && <p className="text-xs text-gray-500">{message}</p>}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={isBusy} className={secondaryButton}>
            Cancel
          </button>

          {isMismatch ? (
            <button
              type="button"
              onClick={onStartNewUpload}
              disabled={isBusy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs
               font-medium text-white hover:bg-indigo-500 transition-colors
               disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <UploadCloudIcon className="h-3.5 w-3.5" />
              Upload {pickedName} as a new file
            </button>
          ) : (
            <button
              type="button"
              onClick={onDeleteAndReupload}
              disabled={isBusy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs
               font-medium text-white hover:bg-red-500 transition-colors
               disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <TrashIcon className="h-3.5 w-3.5" />
              {isBusy ? 'Deleting…' : 'Delete and upload again'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ResumeUploadModal;
